<template>
  <x-form
    ouia-id="form-skill"
    @submit="submit"
    :disabled="sending"
    :prevent-submit="preventSubmit"
    :submit-text="$t('Invia')"
  >
    <form-group :name="$t('Quali sono le tue skill?')" required>
      <template v-if="asyncCompany.updating">
        <pf-skeleton width="50%" />
        <pf-skeleton width="45%" />
      </template>
      <pf-switch-list
        v-else
        v-model="company.requirements"
        name="requirements[]"
        :items="skills"
        label-field="nome"
        value-field="id"
      />
    </form-group>

    <form-group
      v-if="mainSkills.length"
      :name="$t('Skill Principale')"
      :helper-text="$t('Quale sai fare meglio? Scegline una, che considereremo la tua skill principale.')"
      required
    >
      <template v-for="skill in mainSkills" :key="skill.id">
        <pf-radio
          :checked="company.id_main_skill === skill.id"
          @change="company.id_main_skill = skill.id"
          :label="skill.nome"
          name="id_main_skill"
          value="skill.id"
          required
        />
      </template>
    </form-group>

    <form-group :name="$t('Con quali tecnologie lavori principalmente?')">
      <template v-if="asyncCompany.updating">
        <pf-skeleton width="30%" />
        <pf-skeleton width="35%" />
        <pf-skeleton width="45%" />
        <pf-skeleton width="32%" />
      </template>
      <pf-switch-list
        v-else
        v-model="company.requirements"
        name="requirements[]"
        :items="techSkills"
        label-field="nome"
        value-field="id"
      />
    </form-group>

    <form-group :name="$t('In quali settori merceologici operi principalmente?')">
      <template v-if="asyncCompany.updating">
        <pf-skeleton width="36%" />
        <pf-skeleton width="50%" />
      </template>
      <pf-switch-list
        v-else
        v-model="company.requirements"
        name="requirements[]"
        :items="businessSectors"
        label-field="nome"
        value-field="id"
      />
    </form-group>

    <template #actions>
      <pf-button variant="secondary" @click="askReset">
        {{ $t('Ripristina') }}
      </pf-button>
    </template>
  </x-form>
</template>

<style lang="scss" scoped>
:deep(.pf-v6-c-switch) {
  min-width: 250px;
}
</style>

<script lang="ts" setup>
import { useCompany } from '../utils';
import { RequirementsResource } from '../resources';
import { asyncComputedList } from '@common/asyncComputed';
import { $t as _t } from '@/i18n';
import { computed, ref, unref, watch } from 'vue';
import cloneDeep from 'lodash-es/cloneDeep';
import { CompanyResource } from '@/resources';
import { useAlertStore } from '@/store/alerts';
import { useLoginStore } from '@/store/login';
import PfSwitchList from '@/common/PfSwitchList.vue';

const props = defineProps<{
  embedded?: boolean;
  required?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit'): void
}>();

const origCompany = useCompany();
const asyncCompany = origCompany.state;
const company = ref(cloneDeep(unref(origCompany.result.value)));
const wasEmpty = ref(false);
const sending= ref(false);
const login = useLoginStore();

const reset = () => {
  company.value = cloneDeep(unref(origCompany.result.value));
  wasEmpty.value = company.value.requirements.length === 0;
};

const { result: requirements } = asyncComputedList(() => new RequirementsResource().get());

const preventSubmit = computed(() => props.required && (!company.value.requirements.length || !company.value.id_main_skill));

const skills = computed(() => requirements.value.filter(r => r.tipo === 'skill').sort((a, b) => a.nome < b.nome ? -1 : 1 ));
const mainSkills = computed(() => skills.value.filter(x => company.value.requirements.includes(x.id)));
const techSkills = computed(() => requirements.value.filter(r => r.tipo === 'tech skill').sort((a, b) => a.nome < b.nome ? -1 : 1 ));
const businessSectors = computed(() => requirements.value.filter(r => r.tipo === 'business sector').sort((a, b) => a.nome < b.nome ? -1 : 1 ));

watch(origCompany.result, reset);

watch(mainSkills, () => {
  if (company.value.id_main_skill && !company.value.requirements.includes(company.value.id_main_skill)) {
    company.value.id_main_skill = null;
  }
});

async function submit() {
  sending.value = true;
  try {
    if (!props.embedded && !wasEmpty.value && !company.value.requirements.length && !confirm(_t("Confermando l'invio del form eliminerai tutte skill selezionate. Vuoi procedere?"))) {
      return;
    }
    company.value = await new CompanyResource().save(company.value.id, {
      id_main_skill: company.value.id_main_skill,
      requirements: company.value.requirements,
    });
    useAlertStore().add({ title: _t('Skill aggiornate'), type: 'success', toast: true});
  } finally {
    sending.value = false;
  }
  login.refresh();
  emit('submit');
};

function askReset() {
  if (confirm(_t('Vuoi ripristinare i valori iniziali?'))) {
    reset();
  }
};
</script>
